import React from "react";
import { IUser, IUserAttributes } from "../../types";
import { WANTABLE_API_BASE_URL, CACHED_TOKEN_KEY, CACHED_USER_DATA_KEY, TOKEN_REMOVED } from "@wantable/Config"
import { cacheShopifyToken, cacheUserData, getCachedUserData, getCachedAccessToken, checkIfLoggedIn, clearUserSessionCache } from "@wantable/Shared";

const handleErrorResponse = async (response: Response) => {
  const result: any = await response.json();
  let errorMessage = "";
  if(result.errors){
    if (Array.isArray(result.errors)) {
      errorMessage = result.errors.reduce(
        (previousValue: string, currentValue: string) =>
          previousValue + " " + currentValue,
        ""
      );
    } else {
      Object.entries(result.errors).forEach(([key, errors]) => {
        errorMessage += key + ": " + (errors as Array<string>).join(", ");
      });
    }
  } else {
    errorMessage = await response.text();
  }
  throw new Error(errorMessage);
};

export const getAccessToken = (): {
  token: string;
  shopifyUID: string;
} | null => {
  const tokenData = getCachedAccessToken()

  if (!tokenData) return null;

  const { shopifyToken, shopifyUID } = tokenData;

  return { token: shopifyToken.token, shopifyUID };
};

const useAuth = () => {
  const login = async (email: string, password: string) => {
    const response = await fetch(`${WANTABLE_API_BASE_URL}/users/login`, {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const result: any = await response.json();

    cacheUserData(result);
    cacheShopifyToken(result);
  };

  const logout = () => {
    clearUserSessionCache();

    const event = new Event(TOKEN_REMOVED);
    window.dispatchEvent(event);
    window.location.href = "/account/logout"
  };

  const isUserLoggedIn = () => {
    return checkIfLoggedIn();
  };

  const getUserData = async (): Promise<IUserAttributes | null> => {
    const userData = getCachedUserData();

    if (!userData) {
      const tokenData = getAccessToken();

      const response = await fetch(`${WANTABLE_API_BASE_URL}/users/current`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Shopify-Multipass-Token": tokenData?.token || "",
          "Shopify-UID": tokenData?.shopifyUID || "",
        },
      });

      const result = await response.json();

      if (!response.ok) {
        await handleErrorResponse(response);
      }

      cacheUserData(result);
      cacheShopifyToken(result);

      return result.data.attributes;
    }

    return userData;
  };

  const register = async (
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ) => {
    const response = await fetch(`${WANTABLE_API_BASE_URL}/users/register`, {
      method: "POST",
      body: JSON.stringify({
        user: {
          first_name: firstName,
          last_name: lastName,
          email,
          password,
          password_confirmation: password,
          is_mail_list_subscriber: true,
        },
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const result: any = await response.json();

    cacheUserData(result);
    cacheShopifyToken(result);
  };

  const initiatePasswordReset = async (email: string) => {
    const response = await fetch(`${WANTABLE_API_BASE_URL}/users/initiate_password_reset`, {
      method: "POST",
      body: JSON.stringify({
        user: {
          email: email
        }
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      await handleErrorResponse(response);
    }
  };

  const resetPassword = async (resetPasswordToken: string, password: string, passwordConfirmation: string) => {
    const response = await fetch(`${WANTABLE_API_BASE_URL}/users/reset_password`, {
      method: "POST",
      body: JSON.stringify({
        user: {
          reset_password_token: resetPasswordToken,
          password: password,
          password_confirmation: passwordConfirmation,
        }
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const result: any = await response.json();

    cacheUserData(result);
    cacheShopifyToken(result);
  };


  return { login, register, getUserData, isUserLoggedIn, logout, initiatePasswordReset, resetPassword };
};

export default useAuth;
